import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faTrash,
    faEuroSign,
    faCoins,
    faCreditCard,
    faCalendar,
} from "@fortawesome/free-solid-svg-icons";

import PaginatorComponent from "../general/auxiliar/ui/PaginatorComponent";
import LoadingTable from "../general/auxiliar/table/LoadingTable";
import EmptyTable from "../general/auxiliar/table/EmptyTable";
import OverviewTable from "../general/auxiliar/table/OverviewTable";
import OverviewCardList from "../general/auxiliar/table/OverviewCard";
import TableBadge from "../general/auxiliar/table/TableBadge";
import usePaginationFilterEffects from "../../auxiliar/customHooks/usePaginationFilterEffects";
import { getAllCharges, deleteCharge } from "../../actions/charge";
import { handleSubmitWithDispatch, getTableState } from "../../auxiliar/auxFunctions";

const ChargeCardList = ({ charges, refresh, dispatch }) => {
    const renderTitle = (charge) => `${Number(charge.paid).toFixed(2)}€`;

    const renderSubtitle = (charge) => (
        <div className="flex items-center gap-2 mt-1">
            <TableBadge variant={Number(charge.paid) >= 0 ? "success" : "error"} size="xs">
                {Number(charge.paid) >= 0 ? "Pago" : "Devolución"}
            </TableBadge>
        </div>
    );

    const renderFooter = (charge) => (
        <div className="space-y-2 text-xs sm:text-sm">
            <div className="flex items-center gap-2 text-base-content/70">
                <FontAwesomeIcon
                    icon={faCalendar}
                    className="w-3 h-3 text-base-content/40 flex-shrink-0"
                />
                <span className="truncate">{charge.charge_date}</span>
            </div>
            <div className="flex items-center gap-2 text-base-content/70">
                <FontAwesomeIcon
                    icon={faCreditCard}
                    className="w-3 h-3 text-base-content/40 flex-shrink-0"
                />
                <span className="truncate">{charge.payment_method}</span>
            </div>
        </div>
    );

    const renderActions = (charge, id) => (
        <button
            type="button"
            className="btn btn-error btn-xs sm:btn-sm text-xs sm:text-sm"
            onClick={handleSubmitWithDispatch(dispatch, deleteCharge, id, refresh)}
        >
            <FontAwesomeIcon icon={faTrash} className="mr-1 sm:mr-2 text-xs sm:text-sm" />
            <span className="sm:hidden">Eliminar</span>
            <span className="hidden sm:inline">Eliminar</span>
        </button>
    );

    return (
        <OverviewCardList
            data={charges}
            renderTitle={renderTitle}
            renderSubtitle={renderSubtitle}
            renderFooter={renderFooter}
            renderActions={renderActions}
        />
    );
};

const OverviewCharges = ({ cashRegisterId, boolRefresh, refresh }) => {
    const dispatch = useDispatch();

    const activeClinicId = useSelector((state) => state.activeClinic.id);
    const [charges, setCharges] = useState({ waiting: true });
    const [filter, setFilter] = useState({
        cash_register_id: cashRegisterId,
        clinic_id: activeClinicId,
        order: "charge_date",
        per_page: 5,
        page: 1,
    });

    const disabled = usePaginationFilterEffects(
        filter,
        undefined,
        getAllCharges,
        setCharges,
        setFilter,
        boolRefresh,
    );

    const columns = [
        {
            label: "Cantidad",
            icon: faEuroSign,
            width: "w-[20%]",
        },
        {
            label: "Tipo",
            icon: faCoins,
            width: "w-[20%]",
        },
        {
            label: "Fecha",
            icon: faCalendar,
            width: "w-[25%]",
        },
        {
            label: "Método de pago",
            icon: faCreditCard,
            width: "w-[25%]",
        },
    ];

    const getRowData = (charge, id) => {
        return [
            // Amount
            <span key={`${id}-amount`} className="font-semibold">
                {Number(charge.paid).toFixed(2)}€
            </span>,
            // Type
            <span key={`${id}-type`} className="hidden md:inline">
                {Number(charge.paid) >= 0 ? "Pago" : "Devolución"}
            </span>,
            // Date
            <span key={`${id}-date`} className="text-base-content">
                {charge.charge_date}
            </span>,
            // Payment method
            <span key={`${id}-payment-method`} className="hidden sm:inline text-base-content/70">
                {charge.payment_method}
            </span>,
        ];
    };

    const renderActions = (charge, id) => {
        return (
            <button
                type="button"
                className="btn btn-error btn-sm"
                onClick={handleSubmitWithDispatch(dispatch, deleteCharge, id, refresh)}
            >
                <FontAwesomeIcon icon={faTrash} alt="Eliminar" />
            </button>
        );
    };

    const tableState = getTableState(charges);

    const renderContent = () => {
        switch (tableState) {
            case "loading":
                return <LoadingTable message="Cargando pagos..." />;

            case "empty":
                return (
                    <EmptyTable
                        icon={faEuroSign}
                        title="No hay pagos registrados"
                        description="El paciente todavía no ha realizado ningún pago."
                        showButton={false}
                    />
                );

            case "hasData":
            default:
                return (
                    <>
                        <ChargeCardList charges={charges} refresh={refresh} dispatch={dispatch} />
                        <OverviewTable
                            data={charges}
                            columns={columns}
                            getRowData={getRowData}
                            renderActions={renderActions}
                            actionsWidth="w-[10%]"
                        />
                    </>
                );
        }
    };

    return (
        <div className="my-3">
            {renderContent()}

            <div className="mt-5 flex justify-end">
                <PaginatorComponent
                    pagination={charges.pagination}
                    setFilter={setFilter}
                    filter={filter}
                    disabled={disabled}
                />
            </div>
        </div>
    );
};

export default OverviewCharges;
